import { useCallback, useEffect, useState } from "react";
import api from "../services/api";

export interface AuthUser {
  id: number;
  email: string;
  permissions?: string[];
  created_at: string;
  updated_at: string;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const tokenKey = "auth_token";
  const refreshKey = "refresh_token";

  const setTokens = useCallback(
    (accessToken: string | null, refreshToken?: string | null) => {
      if (accessToken) {
        localStorage.setItem(tokenKey, accessToken);
      } else {
        localStorage.removeItem(tokenKey);
      }
      if (refreshToken !== undefined) {
        if (refreshToken) localStorage.setItem(refreshKey, refreshToken);
        else localStorage.removeItem(refreshKey);
      }
    },
    [],
  );

  const getToken = useCallback(() => localStorage.getItem(tokenKey), []);
  const getRefreshToken = useCallback(
    () => localStorage.getItem(refreshKey),
    [],
  );

  const fetchMe = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api.post("/auth/login", { email, password });
      setTokens(res.data.access_token, res.data.refresh_token);
      await fetchMe();
      return res.data;
    },
    [fetchMe, setTokens],
  );

  const logout = useCallback(() => {
    setTokens(null, null);
    setUser(null);
  }, [setTokens]);

  const refresh = useCallback(async () => {
    const rt = getRefreshToken();
    if (!rt) throw new Error("No refresh token");
    const res = await api.post("/auth/refresh", { refresh_token: rt });
    setTokens(res.data.access_token);
    return res.data;
  }, [getRefreshToken, setTokens]);

  return { user, loading, login, logout, getToken, refresh };
}
